/* Admin · Dashboard (nav label) — page heading "Analytics", per the prototype.
 *
 * KPI tiles, Chart.js charts and the eShop-style tables, fed by /api/admin/analytics.
 * The prototype charts four simulated series; here every mark comes from the real
 * summary, so the two charts are the ones the API can actually back.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react'
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  DoughnutController,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js'

import { admin } from '../../api/client'
import type { AnalyticsSummary, Me, MetricDelta } from '../../api/types'
import { Card, EmptyState, Kpi, PageHead, Skeleton, errorMessage, nf, useToast } from '../../ui/chrome'
import { Icon } from '../../ui/icons'
import { exportCSV } from '../../ui/pdf'

Chart.register(
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  DoughnutController,
  Legend,
  LinearScale,
  Tooltip,
)

Chart.defaults.font.family =
  "'Poppins',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif"

/* Chart series stay inside the spec palette: lime, KBL greens, navy, slate, caution. */
const CHART_COLORS = ['#C3D82F', '#6FA22E', '#1F2C3B', '#5F8F26', '#8A959F', '#8A7326']

/* Is there an earlier period to compare against at all? Separated from the rendering because
 * the answer decides WHERE the text belongs, not just what it says: a real movement is a fact
 * about the business and earns permanent space on the tile, while "there is nothing to compare
 * to" is the absence of one and belongs on hover with the other explanatory copy. */
function hasPrior(delta: MetricDelta): delta is MetricDelta & { delta: number } {
  // A type guard rather than a plain boolean, so the narrowing survives into deltaDesc and the
  // non-null assertions it would otherwise need are unnecessary.
  return delta.delta !== null && delta.delta_unit !== 'none'
}

function deltaDesc(delta: MetricDelta, invert = false): ReactNode {
  if (!hasPrior(delta)) return null
  const up = delta.delta > 0
  const good = invert ? !up : up
  const suffix = delta.delta_unit === 'percentage_points' ? 'pp' : '%'
  return (
    <>
      <span className={good ? 'up' : 'dn'}>
        {up ? '▲' : '▼'} {Math.abs(delta.delta).toFixed(1)}
        {suffix}
      </span>{' '}
      vs previous
    </>
  )
}

export default function AnalyticsScreen({ me }: { me: Me }) {
  const toast = useToast()
  const [days, setDays] = useState(7)
  const [data, setData] = useState<AnalyticsSummary | null>(null)
  const [error, setError] = useState<string | null>(null)

  const volRef = useRef<HTMLCanvasElement>(null)
  const catRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    setData(null)
    setError(null)
    admin
      .analytics(days)
      .then(setData)
      .catch((cause: unknown) => setError(errorMessage(cause)))
  }, [days])


  /* Charts live outside React's render tree; rebuild them whenever the data changes. */
  useEffect(() => {
    if (!data) return
    // Chart's generics make a mixed-type array awkward; only destroy() is called on these.
    const charts: { destroy: () => void }[] = []

    if (volRef.current && data.engagement.length > 0) {
      charts.push(
        new Chart(volRef.current, {
          type: 'bar',
          data: {
            labels: data.engagement.map((p) =>
              new Date(p.day).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
            ),
            datasets: [
              {
                label: 'Active users',
                data: data.engagement.map((p) => p.active_users),
                backgroundColor: '#C3D82F',
                borderRadius: 3,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: { legend: { labels: { boxWidth: 12, padding: 14 } } },
            scales: {
              x: { grid: { color: '#eceee6' } },
              y: { beginAtZero: true, grid: { color: '#eceee6' } },
            },
          },
        }),
      )
    }

    if (catRef.current && data.top_cited_documents.length > 0) {
      charts.push(
        new Chart(catRef.current, {
          type: 'doughnut',
          data: {
            labels: data.top_cited_documents.slice(0, 6).map((d) => d.name),
            datasets: [
              {
                data: data.top_cited_documents.slice(0, 6).map((d) => d.citation_count),
                backgroundColor: CHART_COLORS,
                borderWidth: 2,
                borderColor: '#fff',
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'right',
                labels: { boxWidth: 10, padding: 8, font: { size: 10 } },
              },
            },
          },
        }),
      )
    }

    return () => charts.forEach((c) => c.destroy())
  }, [data])

  function doExport() {
    if (!data) return
    const rows: (string | number)[][] = [
      ['KBL Internal AI Assistant — analytics export'],
      ['Generated', new Date().toLocaleString('en-IN')],
      ['Generated by', me.display_name],
      ['Date range', `Last ${days} days`],
      [],
      ['Top query', 'Volume'],
      ...data.top_queries.map((q) => [q.query, q.count]),
      [],
      ['Top cited document', 'Citations'],
      ...data.top_cited_documents.map((d) => [d.name, d.citation_count]),
    ]
    exportCSV('kbl-assistant-analytics.csv', rows)
    toast('ok', 'Export complete', `kbl-assistant-analytics.csv downloaded.`)
  }

  return (
    <>
      {/* The rail calls this "Dashboard"; the page called itself "Analytics". A screen should
          answer to the name it was clicked by. */}
      <PageHead
        title="Dashboard"
        actions={
          <button type="button" className="btn btn-soft" onClick={doExport} disabled={!data}>
            <Icon name="dl" size={16} /> Export CSV
          </button>
        }
      />

      <div className="filters">
        <div className="field">
          <label htmlFor="afRange">Date range</label>
          <select
            id="afRange"
            className="inp"
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last quarter</option>
          </select>
        </div>
        {data?.department_scope && (
          <div className="field">
            <label>Department</label>
            <input
              className="inp"
              value={data.department_scope}
              disabled
              title={
                data.scope_is_locked
                  ? 'Your admin scope is limited to this department.'
                  : undefined
              }
            />
          </div>
        )}
        <button
          type="button"
          className="btn btn-soft"
          onClick={() => {
            setDays(7)
            toast('info', 'Filters reset')
          }}
        >
          <Icon name="refresh" size={16} /> Reset
        </button>
      </div>

      {error && (
        <div className="alert a-danger">
          <Icon name="alert" size={18} />
          <div>
            <strong>Analytics could not be loaded.</strong>
            {error}
          </div>
        </div>
      )}

      {!data && !error && <Skeleton rows={6} />}

      {data && (
        <>
          {/* Four tiles, four different questions: how much it is used, how many people use
              it, where it fails, how fast it answers.

              "Satisfaction" is gone — it counted thumbs-up as a share of ALL answers, so it
              read 0% whenever nobody happened to rate, which is not the same fact and is the
              one an admin would act on wrongly. "Answered" is gone too: it was
              queries × (100 − unanswered rate), and its own caption restated the answer rate.
              One fact does not need two tiles. */}
          <div className="grid g4 mb16">
            <Kpi
              label="Conversations"
              value={nf(data.queries.value)}
              /* Only a real movement is shown outright. With no earlier period the tile stays
                 clean and explains itself on hover, exactly like the three beside it — the
                 reader is not made to read a non-fact every time they open the page. */
              desc={hasPrior(data.queries) ? deltaDesc(data.queries) : undefined}
              /* Exactly one description line either way. Kpi renders `desc` and `hoverDesc` as
                 two separate rows, so passing both here would make this tile a line taller than
                 the three beside it and break the row's alignment. */
              hoverDesc={hasPrior(data.queries) ? undefined : 'No earlier period to compare with yet'}
            />
            <Kpi
              tone="green"
              label="Active users"
              value={nf(data.unique_users)}
              hoverDesc="Employees who asked a question"
            />
            <Kpi
              tone="danger"
              label="Unanswered rate"
              value={`${data.unanswered_rate.value.toFixed(1)}%`}
              hoverDesc="Questions that got no answer. Goal: under 5%"
            />
            <Kpi
              tone="warn"
              label="Avg resolution"
              value={
                <>
                  {(data.avg_resolution_time_ms.value / 1000).toFixed(1)}
                  <span style={{ fontSize: 15 }}>s</span>
                </>
              }
              hoverDesc="Average time to get an answer"
            />
          </div>

          <div className="grid g-2-1 mb16">
            <Card icon="chart" title="Daily active users">
              <div className="chart-box">
                {data.engagement.length > 0 ? (
                  <canvas ref={volRef} />
                ) : (
                  <EmptyState icon="chart" title="No engagement yet" msg="Activity appears here once employees start asking questions." />
                )}
              </div>
            </Card>
            <Card icon="layers" title="Citations by document">
              <div className="chart-box">
                {data.top_cited_documents.length > 0 ? (
                  <canvas ref={catRef} />
                ) : (
                  <EmptyState icon="book" title="No citations yet" msg="Cited documents appear here." />
                )}
              </div>
            </Card>
          </div>

          <div className="grid g2">
            <Card icon="search" title="Top queries" tight>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Query</th>
                      <th className="num">Volume</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_queries.length === 0 && (
                      <tr>
                        <td colSpan={2} className="tiny muted">
                          No query recorded in this period.
                        </td>
                      </tr>
                    )}
                    {data.top_queries.slice(0, 8).map((q) => (
                      <tr key={q.query}>
                        <td>{q.query}</td>
                        <td className="num">{nf(q.count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            <Card icon="book" title="Top cited documents" tight>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th className="num">Citations</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_cited_documents.length === 0 && (
                      <tr>
                        <td colSpan={2} className="tiny muted">
                          No document has been cited yet.
                        </td>
                      </tr>
                    )}
                    {data.top_cited_documents.slice(0, 8).map((d) => (
                      <tr key={d.id}>
                        <td>
                          <strong>{d.name}</strong>
                        </td>
                        <td className="num">{nf(d.citation_count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </>
      )}
    </>
  )
}
